import { useEffect, useRef } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Kleiner, robuster Event-Sender:
 * - Nutzt `navigator.sendBeacon` bei Exit (überlebt Page-Unload)
 * - Fallback auf fetch mit keepalive
 * - Session-Cookie wird durch `credentials: include` mitgesendet
 */
export const sendAnalyticsEvent = (payload) => {
  try {
    const body = JSON.stringify(payload);
    const url = `${API_URL}/api/analytics/lesson-event`;
    if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
      const blob = new Blob([body], { type: 'application/json' });
      navigator.sendBeacon(url, blob);
      return;
    }
    fetch(url, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true,
    }).catch(() => { /* silent */ });
  } catch { /* silent */ }
};

/**
 * useLessonTracking — sendet lesson_start beim Mount, misst Scroll-Fortschritt
 * und Verweildauer, und sendet lesson_exit beim Unmount/Tab-Wechsel.
 *
 * @param {string} moduleId
 * @param {string} lessonId
 */
export const useLessonTracking = (moduleId, lessonId) => {
  const startedAt = useRef(Date.now());
  const maxProgress = useRef(0);
  const lastSentProgress = useRef(0);
  const sessionId = useRef(null);
  const hasStarted = useRef(false);

  useEffect(() => {
    if (!moduleId || !lessonId) return;

    // Neue Session pro Lektion (überlebt keine Reloads)
    sessionId.current = `${moduleId}-${lessonId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    startedAt.current = Date.now();
    maxProgress.current = 0;
    lastSentProgress.current = 0;

    if (!hasStarted.current) {
      sendAnalyticsEvent({
        event_type: 'lesson_start',
        module_id: moduleId,
        lesson_id: lessonId,
        session_id: sessionId.current,
      });
      hasStarted.current = true;
    }

    // Scroll-Fortschritt tracken
    const handleScroll = () => {
      const doc = document.documentElement;
      const scrolled = doc.scrollTop || window.scrollY || 0;
      const total = (doc.scrollHeight - doc.clientHeight) || 1;
      const pct = Math.min(100, Math.round((scrolled / total) * 100));
      if (pct > maxProgress.current) {
        maxProgress.current = pct;
        // Progress-Pings nur bei signifikantem Zuwachs (>= 20 %-Punkte)
        if (pct - lastSentProgress.current >= 20 || pct >= 95) {
          lastSentProgress.current = pct;
          sendAnalyticsEvent({
            event_type: 'lesson_progress',
            module_id: moduleId,
            lesson_id: lessonId,
            progress_pct: pct,
            time_spent_sec: (Date.now() - startedAt.current) / 1000,
            session_id: sessionId.current,
          });
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    // Exit-Ping bei Tab-Wechsel / Page-Hide (Mobile: pagehide zuverlässiger)
    const sendExit = () => {
      sendAnalyticsEvent({
        event_type: 'lesson_exit',
        module_id: moduleId,
        lesson_id: lessonId,
        progress_pct: maxProgress.current,
        time_spent_sec: (Date.now() - startedAt.current) / 1000,
        session_id: sessionId.current,
      });
    };
    window.addEventListener('pagehide', sendExit);
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') sendExit();
    };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('pagehide', sendExit);
      document.removeEventListener('visibilitychange', onVisibility);
      // Unmount = React-Navigation zu anderer Lektion → Exit
      sendExit();
      hasStarted.current = false;
    };
  }, [moduleId, lessonId]);

  return {
    trackLessonComplete: (pct = 100) => {
      sendAnalyticsEvent({
        event_type: 'lesson_complete',
        module_id: moduleId,
        lesson_id: lessonId,
        progress_pct: pct,
        time_spent_sec: (Date.now() - startedAt.current) / 1000,
        session_id: sessionId.current,
      });
    },
    trackQuestionView: (question_id) => sendAnalyticsEvent({
      event_type: 'quiz_question_view',
      module_id: moduleId,
      lesson_id: lessonId,
      question_id,
      session_id: sessionId.current,
    }),
    trackQuizAbandoned: ({ last_question_id, answered_count, total_count }) => sendAnalyticsEvent({
      event_type: 'quiz_abandoned',
      module_id: moduleId,
      lesson_id: lessonId,
      question_id: last_question_id,
      answered_count,
      total_count,
      session_id: sessionId.current,
    }),
    trackQuizSubmitted: ({ question_id, correct }) => sendAnalyticsEvent({
      event_type: 'quiz_submitted',
      module_id: moduleId,
      lesson_id: lessonId,
      question_id,
      correct,
      session_id: sessionId.current,
    }),
  };
};

export default useLessonTracking;
